import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Plus, Calendar as CalIcon, Mic, FileText, Mail, Video, Briefcase, Newspaper, Bell as BellIcon, Clock, Edit, Trash2, RotateCcw, Download, Search, ChevronLeft, ChevronRight, LayoutList, LayoutGrid, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { fmtDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { UserMultiSelect } from '../components/UserMultiSelect';
import EventWorkspace from '../components/calendar/EventWorkspace';
import { Switch } from '../components/ui/switch';
import { MessageSquare } from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';


const EVENT_ICONS = {
  meeting: CalIcon, podcast: Mic, blog: FileText, newsletter: Mail, webinar: Video,
  event: Briefcase, interview: Newspaper, followup: BellIcon, deadline: Clock,
};

const EVENT_COLORS = {
  meeting: 'bg-blue-100 text-blue-700 border-blue-200',
  podcast: 'bg-purple-100 text-purple-700 border-purple-200',
  blog: 'bg-amber-100 text-amber-700 border-amber-200',
  newsletter: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  webinar: 'bg-pink-100 text-pink-700 border-pink-200',
  event: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  interview: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  followup: 'bg-rose-100 text-rose-700 border-rose-200',
  deadline: 'bg-slate-200 text-slate-700 border-slate-300',
};

const EMPTY = { title: '', event_type: 'meeting', start_at: '', end_at: '', description: '', assigned_to: '', attendees: [], recurring_rule: '', recurring_until: '' };

export default function CalendarPage() {
  const { user: me, isAdmin } = useAuth();
  const isLeadership = me?.role === 'leadership';
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [ev, setEv] = useState(EMPTY);
  const [editEv, setEditEv] = useState(null);
  const [deleteEv, setDeleteEv] = useState(null);
  const [view, setView] = useState('agenda'); // agenda | month | week | day
  const [cursor, setCursor] = useState(new Date());
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [q, setQ] = useState('');
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState([]);
  const [workspaceEv, setWorkspaceEv] = useState(null);
  const [showTasks, setShowTasks] = useState(false);
  const [myTasks, setMyTasks] = useState([]);

  const load = () => api.get('/calendar').then((r) => setEvents(r.data));
  const loadTrash = () => api.get('/calendar/trash').then((r) => setTrash(r.data));

  useEffect(() => {
    load();
    api.get('/users').then((r) => setUsers(r.data));
    api.get('/notification-preferences').then((r) => setShowTasks(!!r.data?.show_tasks_on_calendar)).catch(() => {});
  }, []);

  useEffect(() => {
    if (showTasks) api.get('/tasks').then((r) => setMyTasks(r.data.filter((t) => t.due_date && (t.assignees?.includes(me?.id) || t.assigned_to === me?.id))));
  }, [showTasks, me]);

  const toggleShowTasks = async (v) => {
    setShowTasks(v);
    try {
      const cur = (await api.get('/notification-preferences')).data || {};
      await api.put('/notification-preferences', { ...cur, show_tasks_on_calendar: v });
    } catch { /* preference save best-effort */ }
  };

  const save = async () => {
    if (!ev.title || !ev.start_at) return toast.error('Title and start required');
    try {
      await api.post('/calendar', {
        ...ev,
        assigned_to: ev.assigned_to || null,
        start_at: new Date(ev.start_at).toISOString(),
        end_at: ev.end_at ? new Date(ev.end_at).toISOString() : null,
      });
      setOpen(false); setEv(EMPTY);
      toast.success('Event added'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    try {
      const p = { ...editEv };
      if (p.start_at) p.start_at = new Date(p.start_at).toISOString();
      if (p.end_at) p.end_at = new Date(p.end_at).toISOString();
      await api.put(`/calendar/${editEv.id}`, p);
      toast.success('Event updated');
      setEditEv(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const remove = async () => {
    try {
      await api.delete(`/calendar/${deleteEv.id}`);
      toast.success('Event moved to Trash');
      setDeleteEv(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const restore = async (id) => {
    try {
      await api.post(`/calendar/${id}/restore`);
      toast.success('Restored'); loadTrash(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const perm = async (id) => {
    if (!confirm('Permanently delete? This cannot be undone.')) return;
    try {
      await api.delete(`/calendar/${id}/permanent`);
      toast.success('Deleted forever'); loadTrash();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportICS = async () => {
    const token = localStorage.getItem('aawta_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/export.ics`;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl; a.download = 'aawta-calendar.ics';
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Downloaded .ics — import into Google/Outlook/Apple Calendar');
    } catch { toast.error('Export failed'); }
  };

  const filteredEvents = useMemo(() => {
    const base = events.filter((e) => {
      if (filterAssignee !== 'all' && e.assigned_to !== filterAssignee && !(e.attendees || []).includes(filterAssignee)) return false;
      if (filterType !== 'all' && e.event_type !== filterType) return false;
      if (q && !(e.title || '').toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    if (showTasks && filterType === 'all') {
      const taskEvents = myTasks
        .filter((t) => !['completed', 'approved', 'cancelled'].includes(t.status))
        .filter((t) => !q || t.title.toLowerCase().includes(q.toLowerCase()))
        .map((t) => ({ id: `task-${t.id}`, title: `Task due: ${t.title}`, event_type: 'deadline', start_at: t.due_date, _isTask: true, _taskId: t.id, assigned_to: t.assigned_to }));
      return [...base, ...taskEvents];
    }
    return base;
  }, [events, filterAssignee, filterType, q, showTasks, myTasks]);

  const canEditEvent = (e) => {
    if (e._isTask) return false;
    if (me?.role === 'intern') return false;
    if (me?.role === 'leadership' || me?.role === 'director') return true;
    return e.created_by === me?.id || e.assigned_to === me?.id;
  };

  const openEdit = (e) => setEditEv({
    ...e,
    start_at: e.start_at ? e.start_at.slice(0, 16) : '',
    end_at: e.end_at ? e.end_at.slice(0, 16) : '',
  });

  const userName = (id) => users.find((u) => u.id === id)?.full_name || '—';

  // === Month view helpers ===
  const monthDays = useMemo(() => {
    const y = cursor.getFullYear(); const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const startWeekday = first.getDay();
    const daysIn = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsOnDay = (day) => filteredEvents.filter((e) => new Date(e.start_at).toDateString() === day.toDateString());

  // === Group by date (agenda) ===
  const byDate = filteredEvents.reduce((acc, e) => {
    const day = new Date(e.start_at).toDateString();
    (acc[day] = acc[day] || []).push(e);
    return acc;
  }, {});
  const sortedDays = Object.keys(byDate).sort((a, b) => new Date(a) - new Date(b));

  const shiftCursor = (delta) => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(d.getMonth() + delta);
    else if (view === 'week') d.setDate(d.getDate() + 7 * delta);
    else if (view === 'day') d.setDate(d.getDate() + delta);
    setCursor(d);
  };

  const weekStart = useMemo(() => {
    const d = new Date(cursor); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d;
  }, [cursor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
  }), [weekStart]);

  return (
    <div className="space-y-4" data-testid="calendar-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 w-full min-w-0">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input placeholder="Search events…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-56" data-testid="calendar-search" />
          </div>
          <Select value={filterAssignee} onValueChange={setFilterAssignee}>
            <SelectTrigger className="w-44" data-testid="calendar-filter-user"><SelectValue placeholder="Filter by user" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All team members</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40" data-testid="calendar-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.keys(EVENT_ICONS).map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-end">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 font-medium mr-1 cursor-pointer">
            <Switch checked={showTasks} onCheckedChange={toggleShowTasks} data-testid="show-tasks-toggle" /> My tasks
          </label>
          <Button variant="outline" onClick={exportICS} data-testid="calendar-export-ics"><Download className="w-4 h-4 mr-2" /> Export .ics</Button>
          {isLeadership && (
            <Button variant="outline" onClick={() => { setTrashOpen(true); loadTrash(); }} data-testid="calendar-trash-button"><Trash2 className="w-4 h-4 mr-2" /> Trash</Button>
          )}
          {me?.role !== 'intern' && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-magenta hover:bg-magenta/90" data-testid="new-event-button"><Plus className="w-4 h-4 mr-2" />Add Event</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Schedule Event</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">Title</Label><Input value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} data-testid="event-title" /></div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={ev.event_type} onValueChange={(v) => setEv({ ...ev, event_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.keys(EVENT_ICONS).map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Start</Label><Input type="datetime-local" onChange={(e) => setEv({ ...ev, start_at: e.target.value })} data-testid="event-start" /></div>
                    <div><Label className="text-xs">End</Label><Input type="datetime-local" onChange={(e) => setEv({ ...ev, end_at: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-xs">Description</Label><RichTextEditor value={ev.description} onChange={(v) => setEv({ ...ev, description: v })} minHeight={90} label="Event Description" testId="event-desc-editor" /></div>
                  <div>
                    <Label className="text-xs">Assign To</Label>
                    <Select value={ev.assigned_to} onValueChange={(v) => setEv({ ...ev, assigned_to: v === '_all' ? '' : v })}>
                      <SelectTrigger data-testid="event-assignee"><SelectValue placeholder="Everyone (org-wide event)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">Everyone (org-wide event)</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Meeting Attendees (internal)</Label>
                    <UserMultiSelect users={users} value={ev.attendees} onChange={(v) => setEv({ ...ev, attendees: v })} placeholder="Invite team members" testId="event-attendees" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Recurring</Label>
                      <Select value={ev.recurring_rule} onValueChange={(v) => setEv({ ...ev, recurring_rule: v === 'none' ? '' : v })}>
                        <SelectTrigger data-testid="event-recurring"><SelectValue placeholder="One time" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">One time</SelectItem>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="WEEKLY">Weekly</SelectItem>
                          <SelectItem value="MONTHLY">Monthly</SelectItem>
                          <SelectItem value="YEARLY">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {ev.recurring_rule && (
                      <div><Label className="text-xs">Repeat until</Label><Input type="date" onChange={(e) => setEv({ ...ev, recurring_until: e.target.value })} /></div>
                    )}
                  </div>
                </div>
                <DialogFooter><Button onClick={save} className="bg-magenta hover:bg-magenta/90" data-testid="save-event">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* View tabs */}
      <Tabs value={view} onValueChange={setView}>
        <div className="flex items-center justify-between">
          <TabsList data-testid="calendar-view-tabs">
            <TabsTrigger value="agenda" data-testid="view-agenda"><LayoutList className="w-4 h-4 mr-1" /> Agenda</TabsTrigger>
            <TabsTrigger value="month" data-testid="view-month"><LayoutGrid className="w-4 h-4 mr-1" /> Month</TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week"><CalendarDays className="w-4 h-4 mr-1" /> Week</TabsTrigger>
            <TabsTrigger value="day" data-testid="view-day"><CalIcon className="w-4 h-4 mr-1" /> Day</TabsTrigger>
          </TabsList>
          {view !== 'agenda' && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => shiftCursor(-1)}><ChevronLeft className="w-4 h-4" /></Button>
              <div className="text-sm font-semibold text-navy px-2">
                {view === 'month' && cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                {view === 'week' && `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                {view === 'day' && cursor.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <Button variant="ghost" size="sm" onClick={() => shiftCursor(1)}><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
            </div>
          )}
        </div>

        <TabsContent value="agenda" className="mt-4">
          <div className="space-y-4">
            {sortedDays.map((day) => (
              <div key={day} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-5 py-3 border-b border-slate-100">
                  <div className="font-heading font-bold text-navy text-sm">
                    {new Date(day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {byDate[day].map((e) => {
                    const Icon = EVENT_ICONS[e.event_type] || CalIcon;
                    return (
                      <div key={e.id} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 group" data-testid={`event-${e.id}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${EVENT_COLORS[e.event_type]}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-navy truncate">{e.title}</div>
                          <div className="text-xs text-slate-500 capitalize">
                            {e._isTask ? 'task due date' : e.event_type} · {userName(e.assigned_to)}
                            {(e.attendees?.length || 0) > 0 && <> · {e.attendees.length} attendee{e.attendees.length > 1 ? 's' : ''}</>}
                          </div>
                        </div>
                        <div className="text-xs text-slate-600 font-medium">{fmtDateTime(e.start_at)}</div>
                        {!e._isTask && (
                          <Button size="sm" variant="ghost" onClick={() => setWorkspaceEv(e)} title="Meeting workspace" data-testid={`workspace-event-${e.id}`}>
                            <MessageSquare className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canEditEvent(e) && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-event-${e.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteEv(e)} className="text-rose-600" data-testid={`delete-event-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {sortedDays.length === 0 && (
              <div className="bg-white rounded-2xl p-12 text-center text-slate-500">No scheduled events</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="month" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden overflow-x-auto">
            <div className="min-w-[560px]">
            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="px-2 py-2 text-xs font-heading uppercase tracking-wider text-slate-500 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {monthDays.map((day, idx) => {
                const items = day ? eventsOnDay(day) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                return (
                  <div key={idx} className={`min-h-[110px] border-r border-b border-slate-100 p-1.5 ${isToday ? 'bg-magenta/5' : ''}`}>
                    {day && (
                      <>
                        <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-magenta' : 'text-slate-600'}`}>{day.getDate()}</div>
                        <div className="space-y-1">
                          {items.slice(0, 3).map((e) => (
                            <button key={e.id} onClick={() => canEditEvent(e) && openEdit(e)}
                              className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded border ${EVENT_COLORS[e.event_type]} truncate`}
                              data-testid={`month-event-${e.id}`}>
                              {new Date(e.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} {e.title}
                            </button>
                          ))}
                          {items.length > 3 && <div className="text-[10px] text-slate-400">+{items.length - 3} more</div>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="week" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden overflow-x-auto">
            <div className="min-w-[640px]">
            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
              {weekDays.map((d) => (
                <div key={d.toISOString()} className="px-3 py-2 text-xs text-center border-r border-slate-100">
                  <div className="font-heading uppercase tracking-wider text-slate-500">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className={`font-bold ${d.toDateString() === new Date().toDateString() ? 'text-magenta' : 'text-navy'}`}>{d.getDate()}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDays.map((d) => {
                const items = eventsOnDay(d);
                return (
                  <div key={d.toISOString()} className="border-r border-slate-100 p-2 space-y-1">
                    {items.map((e) => (
                      <button key={e.id} onClick={() => canEditEvent(e) && openEdit(e)}
                        className={`w-full text-left text-[11px] px-2 py-1.5 rounded border ${EVENT_COLORS[e.event_type]}`}
                        data-testid={`week-event-${e.id}`}>
                        <div className="font-semibold">{new Date(e.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
                        <div className="truncate">{e.title}</div>
                      </button>
                    ))}
                    {items.length === 0 && <div className="text-[10px] text-slate-300 text-center pt-4">—</div>}
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="day" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <div className="space-y-2">
              {eventsOnDay(cursor).map((e) => {
                const Icon = EVENT_ICONS[e.event_type] || CalIcon;
                return (
                  <div key={e.id} className={`flex gap-3 items-center rounded-xl border p-3 ${EVENT_COLORS[e.event_type]}`} data-testid={`day-event-${e.id}`}>
                    <Icon className="w-5 h-5" />
                    <div className="flex-1">
                      <div className="font-semibold text-navy">{e.title}</div>
                      <div className="text-xs text-slate-600">{fmtDateTime(e.start_at)} · {userName(e.assigned_to)}</div>
                    </div>
                    {!e._isTask && (
                      <Button size="sm" variant="ghost" onClick={() => setWorkspaceEv(e)} title="Meeting workspace"><MessageSquare className="w-3.5 h-3.5" /></Button>
                    )}
                    {canEditEvent(e) && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(e)}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteEv(e)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                );
              })}
              {eventsOnDay(cursor).length === 0 && <div className="text-center text-slate-500 py-10">No events on this day</div>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Meeting workspace */}
      <EventWorkspace eventId={workspaceEv?.id} open={!!workspaceEv} onOpenChange={(v) => !v && setWorkspaceEv(null)} users={users} />

      {/* Edit dialog */}
      <Dialog open={!!editEv} onOpenChange={(v) => !v && setEditEv(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-event-dialog">
          <DialogHeader><DialogTitle>Edit event</DialogTitle></DialogHeader>
          {editEv && (
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label><Input value={editEv.title} onChange={(e) => setEditEv({ ...editEv, title: e.target.value })} data-testid="edit-event-title" /></div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={editEv.event_type} onValueChange={(v) => setEditEv({ ...editEv, event_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(EVENT_ICONS).map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Start</Label><Input type="datetime-local" value={editEv.start_at} onChange={(e) => setEditEv({ ...editEv, start_at: e.target.value })} /></div>
                <div><Label className="text-xs">End</Label><Input type="datetime-local" value={editEv.end_at || ''} onChange={(e) => setEditEv({ ...editEv, end_at: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs">Description</Label><RichTextEditor value={editEv.description || ''} onChange={(v) => setEditEv({ ...editEv, description: v })} minHeight={90} label="Event Description" testId="edit-event-desc-editor" /></div>
              <div>
                <Label className="text-xs">Assign To</Label>
                <Select value={editEv.assigned_to || '_all'} onValueChange={(v) => setEditEv({ ...editEv, assigned_to: v === '_all' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Everyone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Everyone (org-wide event)</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Meeting Attendees (internal)</Label>
                <UserMultiSelect users={users} value={editEv.attendees || []} onChange={(v) => setEditEv({ ...editEv, attendees: v })} placeholder="Invite team members" testId="edit-event-attendees" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEv(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-magenta hover:bg-magenta/90" data-testid="save-edit-event">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteEv} onOpenChange={(v) => !v && setDeleteEv(null)}>
        <DialogContent data-testid="delete-event-dialog">
          <DialogHeader>
            <DialogTitle>Are you sure you want to delete this calendar event?</DialogTitle>
            <DialogDescription>
              <b>{deleteEv?.title}</b> will be moved to the Calendar Trash. Leadership can restore it within 30 days; after that it is permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteEv(null)} data-testid="cancel-delete-event">Cancel</Button>
            <Button onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-event">Delete Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trash dialog */}
      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="max-w-2xl" data-testid="calendar-trash-dialog">
          <DialogHeader>
            <DialogTitle>Calendar Trash</DialogTitle>
            <DialogDescription>Events deleted here are auto-purged after 30 days. Leadership only.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {trash.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">Trash is empty.</div>}
            {trash.map((e) => (
              <div key={e.id} className="flex items-center justify-between border-b border-slate-100 py-2" data-testid={`trash-event-${e.id}`}>
                <div>
                  <div className="font-semibold text-sm text-navy">{e.title}</div>
                  <div className="text-xs text-slate-500">{fmtDateTime(e.start_at)} · deleted {fmtDateTime(e.deleted_at)}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => restore(e.id)} data-testid={`restore-event-${e.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore</Button>
                  <Button size="sm" variant="outline" onClick={() => perm(e.id)} className="text-rose-600" data-testid={`permdelete-event-${e.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete forever</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
